import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #17a2b8;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #e0f7fa, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #17a2b8;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const WordsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
  padding: 10px;
  background: #f0f8ff;
  border-radius: 4px;
  min-height: 50px;
`;

const WordTag = styled.span`
  background: #17a2b8;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  
  button {
    background: rgba(255, 255, 255, 0.3);
    border: none;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    font-size: 14px;
    padding: 0;
    
    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`;

const AddWordInput = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
`;

interface WriteSentenceFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const WriteSentenceForm: React.FC<WriteSentenceFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      return typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : {
            words: [],
            correct_sentence: '',
          };
    } catch {
      return {
        words: [],
        correct_sentence: '',
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());
  const [newWord, setNewWord] = useState('');

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handleCorrectSentenceChange = (value: string) => {
    setContentData({
      ...contentData,
      correct_sentence: value,
    });
  };

  const handleAddWord = () => {
    if (newWord.trim()) {
      setContentData({
        ...contentData,
        words: [...(contentData.words || []), newWord.trim()],
      });
      setNewWord('');
    }
  };

  const handleRemoveWord = (index: number) => {
    const newWords = (contentData.words || []).filter((_: any, i: number) => i !== index);
    setContentData({
      ...contentData,
      words: newWords,
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddWord();
    }
  };

  return (
    <FormBox>
      <h5>✍️ Nội dung câu hỏi - Viết câu hoàn chỉnh từ những từ cho sẵn</h5>
      
      <FormGroup>
        <Label>Các từ cho sẵn: *</Label>
        <AddWordInput>
          <Input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Nhập từ và nhấn Enter hoặc nút Thêm..."
            style={{ flex: 1 }}
          />
          <Button
            color="info"
            size="sm"
            onClick={handleAddWord}
            disabled={!newWord.trim()}
          >
            + Thêm
          </Button>
        </AddWordInput>
        
        <WordsList>
          {(contentData.words || []).length === 0 ? (
            <span style={{ color: '#999', fontSize: '13px' }}>
              Chưa có từ nào. Nhập từ ở trên và nhấn Thêm.
            </span>
          ) : (
            (contentData.words || []).map((word: string, index: number) => (
              <WordTag key={index}>
                {word}
                <button type="button" onClick={() => handleRemoveWord(index)}>×</button>
              </WordTag>
            ))
          )}
        </WordsList>
        <small className="form-text text-muted">
          💡 Học sinh sẽ sắp xếp các từ này thành câu hoàn chỉnh
        </small>
      </FormGroup>

      <FormGroup>
        <Label>Câu trả lời đúng: *</Label>
        <Input
          type="textarea"
          value={contentData.correct_sentence || ''}
          onChange={(e) => handleCorrectSentenceChange(e.target.value)}
          placeholder="Ví dụ: What are you doing?"
          rows={2}
        />
        <small className="form-text text-muted">
          Nhập câu hoàn chỉnh đúng mà học sinh cần viết
        </small>
      </FormGroup>

      {/* Preview */}
      <div style={{ 
        background: '#e0f2f1', 
        padding: '12px', 
        borderRadius: '4px',
        marginTop: '15px'
      }}>
        <strong style={{ fontSize: '13px', color: '#00695c' }}>📋 Xem trước:</strong>
        <div style={{ marginTop: '8px', fontSize: '14px' }}>
          <div style={{ marginBottom: '8px' }}>
            <em>Các từ:</em>{' '}
            {(contentData.words || []).join(', ') || '(chưa có từ)'}
          </div>
          <div>
            <em>Đáp án:</em>{' '}
            <strong>{contentData.correct_sentence || '(chưa nhập)'}</strong>
          </div>
        </div>
      </div>

      {/* Debug JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default WriteSentenceForm;
